import React, { Dispatch, SetStateAction } from "react";
import { Box } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AppToolbar from "./AppToolbar";
import SelectionMenu from "./SelectionMenu";
import ActionMenu from "./ActionMenu";
import OptionsMenu from "./OptionsMenu";

interface ToolbarSectionProps {
  mode: "light" | "dark";
  menuUserDarkTheme: string;
  menuUserWhiteTheme: string;
  theme: any;
  selectedItems: Set<string>;
  setSelectedItems: Dispatch<SetStateAction<Set<string>>>;
  isAllSelected: boolean;
  items: any[];
  clearSelectedItems: () => void;
  addStatusMessage: (message: string) => void;
  setHoverStatusMessage: (message: string | null) => void;
  currentPath: string;
  rootFolder: string;
  renameItemPath: string | null;
  setRenameItemPath: (path: string | null) => void;
  toggleColorMode: () => void;
  fetchFolders: (path?: string) => Promise<void>;
}

const ToolbarSection: React.FC<ToolbarSectionProps> = ({
  mode,
  menuUserDarkTheme,
  menuUserWhiteTheme,
  theme,
  selectedItems,
  setSelectedItems,
  isAllSelected,
  items,
  clearSelectedItems,
  addStatusMessage,
  setHoverStatusMessage,
  currentPath,
  rootFolder,
  renameItemPath,
  setRenameItemPath,
  toggleColorMode,
  fetchFolders,
}) => {
  // Implementações locais das funções ausentes
  const handleDownloadClick = (filePath: string) => {
    document.dispatchEvent(new CustomEvent("triggerDownload", { detail: filePath }));
  };

  const handleUpload = () => {
    document.dispatchEvent(new CustomEvent("triggerUpload"));
  };

  const handleCreateFolder = () => {
    document.dispatchEvent(new CustomEvent("triggerCreateFolder"));
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        justifyContent: "flex-end",
        padding: 1,
        paddingRight: 3,
        backgroundColor: mode === "dark" ? menuUserDarkTheme : menuUserWhiteTheme,
        borderRadius: 1,
        mb: 2,
        transition: "background-color 0.8s ease-in-out",
      }}
    >
      <AppToolbar
        selectedItems={selectedItems}
        onDownloadFile={handleDownloadClick}
        onUploadFile={handleUpload}
        onCreateFolder={handleCreateFolder}
        fetchFolders={fetchFolders}
        onStatusChange={setHoverStatusMessage}
      />

      <DragIndicatorIcon
        sx={{
          color: theme.palette.text.primary,
          cursor: "default",
          "&:hover": { color: theme.palette.text.primary },
          transition: "background-color 0.8s ease-in-out",
        }}
      />
      <SelectionMenu
        handleSelectAll={() => {
          const allItems = new Set(items.map((item) => item.path));
          setSelectedItems(allItems);
          addStatusMessage("Todos os itens selecionados.");
        }}
        handleDeselectAll={() => {
          setSelectedItems(new Set());
          addStatusMessage("Seleção removida.");
        }}
        handleInvertSelection={() => {
          const invertedSelection = new Set(
            items.map((item) => item.path).filter((path) => !selectedItems.has(path))
          );
          setSelectedItems(invertedSelection);
          addStatusMessage("Seleção invertida.");
        }}
        isAnySelected={selectedItems.size > 0}
        isAllSelected={isAllSelected}
        onStatusChange={(message: string | null) => setHoverStatusMessage(message)}
      />
      <Box
        sx={{
          borderLeft: "1px solid gray",
          height: "18px",
        }}
      />
      <ActionMenu
        parentFolderPath={currentPath}
        selectedItems={selectedItems}
        clearSelectedItems={() => {
          clearSelectedItems();
          addStatusMessage("Seleção limpa.");
        }}
        setSelectedItems={setSelectedItems}
        rootFolder={rootFolder}
        renameTriggeredItem={renameItemPath}
        onRenameComplete={() => {
          setRenameItemPath(null);
          addStatusMessage("Renomeação concluída.");
        }}
        onStatusChange={(message: string | null) => {
          if (message) {
            setHoverStatusMessage(message);
          } else {
            setHoverStatusMessage(null);
          }
        }}
        fetchFolders={fetchFolders}
      />
      <Box
        sx={{
          borderLeft: "1px solid gray",
          height: "18px",
        }}
      />
      <OptionsMenu toggleColorMode={toggleColorMode} />
      <DragIndicatorIcon
        sx={{
          color: theme.palette.text.primary,
          cursor: "default",
          "&:hover": { color: theme.palette.text.primary },
          transition: "background-color 0.8s ease-in-out",
        }}
      />
    </Box>
  );
};

export default ToolbarSection;
